/* import __COLOCATED_TEMPLATE__ from './sq-super-table.hbs.js'; */
var _dec, _dec2, _dec3, _class, _descriptor, _dec4, _dec5, _dec6, _dec7, _class3, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { or, readOnly, not, sum, mapBy, reads } from '@ember/object/computed';
import { htmlSafe } from '@ember/template';
import Component from '@ember/component';
import EmberObject, { computed } from '@ember/object';
import { t } from 'i18n';
import { tracked } from '@glimmer/tracking';

/*
  What's up? I'm SqSuperTableComponent.

  I wrap around my friend SqLazyListComponent. I'm somewhat like Addepar's
  EmberTable, but I'm written with Ember 1.13 in mind from the beginning and I
  don't need support for older browsers, so I'm lighter on the dependencies.

  At the bottom of this file I provide a Column class that you can use to
  specify the behavior of my column rendering. I use SqSuperTableRowComponent
  to render the `columns` you give me for each row in the `content` you provide.
  Each column's `getCellClasses` and `getCellContent` methods will be called
  with the rows that are currently visible.

  My markup looks like this:

  {{sq-super-table columns=columns
                   content=content
                   rowHeight=60
                   columnPersistKey="dashboard.yourFeatureName"
                   rowAction=(action "rowAction")}}

  The 'columnPersistKey' property is optional. The action gets called with the
  id of the clicked row. Rows can have an 'isSelected' property which will
  highlight them.

  See also SqSuperTableAutoViewportComponent for a version of me that adapts to
  the maximum available space as you scroll around.
*/
// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['super-table', 'table--flex', 'table'],
  attributeBindings: ['style'],
  style: computed('hasFooter', 'hasHeader', 'headerRowHeight', 'rowHeight', 'showAllContent', 'visibleContent.[]', function () {
    if (!this.showAllContent) {
      return null;
    }
    const numberOfRows = this.visibleContent.length;

    // FIXME: 15 is for the potential fixed horizontal scroll bar. This is specifically from the my Mac on chrome
    // This should be generic
    const fixedScrollBarHeightAdjustment = 15;
    const footerBorderHeight = 1;
    let height = numberOfRows * this.rowHeight + fixedScrollBarHeightAdjustment + footerBorderHeight;
    if (this.hasHeader) {
      height += this.headerRowHeight;
    }
    if (this.hasFooter) {
      height += this.rowHeight;
    }
    return `height: ${height}px`;
  }),
  init(...args) {
    this._super(...args);
    if (this.columns === null) {
      this.set('columns', []);
    }
  },
  // Passed in
  columns: null,
  columnPersistKey: null,
  // set to save activeColumns to localStorage
  hasHeader: true,
  hasFooter: true,
  showOptions: false,
  showAllContent: false,
  // To account for the standard 1px border-bottom in table rows,
  // set rowHeight to be 1px greater than the row height set in your CSS
  rowHeight: 49,
  lastItemBecameVisible: null,
  // Defaults to rowHeight, but can be overridden
  headerRowHeight: reads('rowHeight'),
  visibleColumnWidths: mapBy('columns', 'savedWidth'),
  headerFooterWidth: sum('visibleColumnWidths'),
  headerRow: EmberObject.create({
    isHeader: true
  }),
  footerRow: EmberObject.create({
    isFooter: true
  }),
  visibleContent: computed('content.@each.isHidden', function () {
    return (this.content ?? []).filter(item => !item.get('isHidden'));
  }),
  totalRowStyle: computed('headerFooterWidth', function () {
    const headerFooterWidth = this.headerFooterWidth;
    return htmlSafe(`min-width: ${headerFooterWidth}px`);
  })
});

// arbitrarily smallest width to not have dates truncate
export const FIXED_COL_WIDTH = 175;
export let Column = (_dec = computed('isVisible'), _dec2 = computed('headerCellName', 'headerCellKey'), _dec3 = computed('headerCellTooltipKey'), (_class = class Column extends EmberObject {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "cellContentProperty", null);
    // property path of data to pull from row object
    _defineProperty(this, "isVisible", true);
    // This determines the default visiblity for the column. It does not update when
    // the user clicks to show/hide the column
    _defineProperty(this, "isHideable", true);
    _defineProperty(this, "canBeIntervalAggregate", true);
    _defineProperty(this, "isLink", false);
    // A link column will have blue text.
    _defineProperty(this, "savedWidth", FIXED_COL_WIDTH);
    _defineProperty(this, "typeAlign", 'type-align-left');
    _initializerDefineProperty(this, "customCellClasses", _descriptor, this);
  }
  // FIXME: make this configurable? currently used in {{sq-super-table-column-menu}}
  get trackingEventPath() {
    const action = this.isVisible ? 'removeColumn' : 'addColumn';
    return `sales.reports.columns.${action}`;
  }
  get headerCellTitle() {
    return this.headerCellName || this.headerCellKey && t(this.headerCellKey) || '';
  }
  get headerCellTooltip() {
    return this.headerCellTooltipKey && t(this.headerCellTooltipKey);
  }
  getCellContent(row) {
    if (row.get('isHeader')) {
      return this.headerCellTitle;
    } else if (row.get('isFooter')) {
      return this.footerValue;
    } else {
      if (this.cellContentFunction) {
        return this.cellContentFunction(row, this);
      } else {
        return row.get(this.cellContentProperty);
      }
    }
  }
  getCellTooltip(row) {
    if (row.get('isHeader')) {
      return this.headerCellTooltip;
    }
    return undefined;
  }
  getCellClasses(row) {
    const classes = ['table-cell', 'super-table-cell', this.typeAlign, ...this.customCellClasses];
    if (!this.isHideable) {
      classes.push('super-table__fixed-column', 'sq-lazy-list__fixed-x');
    }
    if (this.isLink) {
      classes.push('table-cell--link');
    }
    if (row.get('isHeader')) {
      classes.push('table-cell super-table-cell-header');
      if (!this.isHideable) {
        classes.push('super-table-cell-header--fixed');
      }
    } else if (row.get('isFooter')) {
      classes.push('super-table-cell-footer');
      if (this.footerClasses) {
        classes.push(this.footerClasses);
      }
    } else {
      classes.push('table-cell--selectable');
    }
    return classes.join(' ');
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "customCellClasses", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "trackingEventPath", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "trackingEventPath"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "headerCellTitle", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "headerCellTitle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "headerCellTooltip", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "headerCellTooltip"), _class.prototype)), _class));
export let CustomReportTableRow = (_dec4 = not('rowParent'), _dec5 = readOnly('isTopLevel'), _dec6 = or('isClickable', 'isCollapsible'), _dec7 = computed('isTopLevel', 'rowParent.{isCollapsed,isHidden,isTopLevel}', 'tableIsCollapsible'), (_class3 = class CustomReportTableRow extends EmberObject {
  constructor(...args) {
    super(...args);
    // passed in
    _defineProperty(this, "tableIsCollapsible", false);
    _defineProperty(this, "rowParent", null);
    _defineProperty(this, "isNested", false);
    _defineProperty(this, "isBold", false);
    _defineProperty(this, "isCollapsible", false);
    _defineProperty(this, "isCollapsed", true);
    _defineProperty(this, "isClickable", false);
    _initializerDefineProperty(this, "isTopLevel", _descriptor2, this);
    _initializerDefineProperty(this, "isrowParent", _descriptor3, this);
    // # we only have one level
    _initializerDefineProperty(this, "isSelectable", _descriptor4, this);
  }
  get isHidden() {
    if (this.tableIsCollapsible) {
      if (this.isTopLevel) {
        return false;
      }
      if (this.rowParent.isTopLevel) {
        return this.rowParent.isCollapsed;
      } else {
        return this.rowParent.isHidden;
      }
    } else {
      return false;
    }
  }
}, (_descriptor2 = _applyDecoratedDescriptor(_class3.prototype, "isTopLevel", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class3.prototype, "isrowParent", [_dec5], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class3.prototype, "isSelectable", [_dec6], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class3.prototype, "isHidden", [_dec7], Object.getOwnPropertyDescriptor(_class3.prototype, "isHidden"), _class3.prototype)), _class3));